/**
 * Schedule interval utilities for bulk-edit staggered scheduling.
 * For Media Devoted | Created By Ciro Autiero
 */

/**
 * Parse an HH:MM string into total minutes.
 * Returns 0 for invalid or empty input (treated as "no interval").
 */
export function parseIntervalHHMM(hhMm: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhMm.trim());
  if (!match) return 0;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return 0;
  return hours * 60 + minutes;
}

/**
 * Format total minutes as HH:MM string (e.g. 65 → "01:05").
 */
export function formatIntervalHHMM(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Apply a schedule interval to a base UTC ISO datetime for a given drop index.
 * Drop 0 → base time unchanged.
 * Drop N → base time + N * intervalMinutes.
 */
export function applyScheduleInterval(
  baseUtcIso: string,
  intervalMinutes: number,
  index: number,
): string {
  if (index === 0 || intervalMinutes <= 0) return baseUtcIso;
  const ms = new Date(baseUtcIso).getTime() + intervalMinutes * index * 60_000;
  return new Date(ms).toISOString();
}

/**
 * Compute per-drop executeAfter array for a bulk edit with interval.
 * Returns null entries when no base time is set (pass-through unchanged behaviour).
 */
export function computePerDropExecuteAfters(
  baseUtcIso: string | null | undefined,
  intervalMinutes: number,
  count: number,
): (string | null)[] {
  if (!baseUtcIso || count <= 0) return Array<null>(count).fill(null);
  return Array.from({ length: count }, (_, i) =>
    applyScheduleInterval(baseUtcIso, intervalMinutes, i),
  );
}
